import React, { useEffect, useRef, useState } from "react";
import { motion, useMotionTemplate, useMotionValue, useSpring, useTransform } from "framer-motion";

const CARD_SRC = "/images/brand/moss-matrix-business-card.png";
const CARD_ALT =
  "Moss Matrix business card: circular Irish Sea Moss Gel brand mark with sea moss, figs, blueberries, and a jar of gel.";

/**
 * Premium physical-card prop for Contact — soft stage, float, pointer tilt + sheen.
 * Respects prefers-reduced-motion and coarse pointers.
 */
export default function ContactBusinessCard() {
  const stageRef = useRef(null);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [finePointer, setFinePointer] = useState(true);

  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const springX = useSpring(rawX, { stiffness: 160, damping: 22, mass: 0.45 });
  const springY = useSpring(rawY, { stiffness: 160, damping: 22, mass: 0.45 });

  const rotateX = useTransform(springY, [-0.5, 0.5], [11, -11]);
  const rotateY = useTransform(springX, [-0.5, 0.5], [-14, 14]);
  const sheenX = useTransform(springX, [-0.5, 0.5], [12, 88]);
  const sheenY = useTransform(springY, [-0.5, 0.5], [18, 82]);
  const sheen = useMotionTemplate`radial-gradient(420px circle at ${sheenX}% ${sheenY}%, rgba(255,248,230,0.34) 0%, rgba(201,164,73,0.1) 28%, transparent 58%)`;
  const shadowX = useTransform(springX, [-0.5, 0.5], [18, -18]);
  const shadowY = useTransform(springY, [-0.5, 0.5], [10, 28]);
  const cardShadow = useMotionTemplate`0 ${shadowY}px 40px -18px rgba(0,0,0,0.72), ${shadowX}px 18px 36px -20px rgba(0,0,0,0.45), 0 0 0 1px rgba(201,164,73,0.22)`;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const motionMq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const pointerMq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const sync = () => {
      setReduceMotion(motionMq.matches);
      setFinePointer(pointerMq.matches);
    };
    sync();
    motionMq.addEventListener("change", sync);
    pointerMq.addEventListener("change", sync);
    return () => {
      motionMq.removeEventListener("change", sync);
      pointerMq.removeEventListener("change", sync);
    };
  }, []);

  const interactive = !reduceMotion && finePointer;

  const onPointerMove = (e) => {
    if (!interactive || !stageRef.current) return;
    const rect = stageRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const nx = (e.clientX - rect.left) / rect.width - 0.5;
    const ny = (e.clientY - rect.top) / rect.height - 0.5;
    rawX.set(Math.max(-0.5, Math.min(0.5, nx)));
    rawY.set(Math.max(-0.5, Math.min(0.5, ny)));
  };

  const onPointerLeave = () => {
    rawX.set(0);
    rawY.set(0);
  };

  return (
    <aside className="mm-contact-card" aria-label="Moss Matrix brand card">
      <div
        ref={stageRef}
        className="mm-contact-card__stage"
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerLeave}
      >
        <div className="mm-contact-card__glow" aria-hidden />
        <div className="mm-contact-card__surface" aria-hidden />

        <motion.div
          className={`mm-contact-card__float${reduceMotion ? " is-static" : ""}`}
          style={{ perspective: 1200 }}
        >
          <motion.figure
            className="mm-contact-card__prop"
            style={
              interactive
                ? {
                    rotateX,
                    rotateY,
                    transformStyle: "preserve-3d",
                    boxShadow: cardShadow,
                  }
                : { transformStyle: "preserve-3d" }
            }
          >
            <img
              src={CARD_SRC}
              alt={CARD_ALT}
              width={1024}
              height={662}
              className="mm-contact-card__img"
              loading="eager"
              decoding="async"
              draggable={false}
            />
            {interactive ? (
              <motion.span className="mm-contact-card__sheen" style={{ background: sheen }} aria-hidden />
            ) : null}
            <span className="mm-contact-card__edge" aria-hidden />
          </motion.figure>
        </motion.div>
      </div>
      <p className="mm-contact-card__caption">Moss Matrix brand card — Irish Sea Moss Gel.</p>
    </aside>
  );
}
